import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Layers,
  Users,
  BarChart3,
  Globe,
  Target,
  DollarSign,
  LogOut,
  Settings,
  ChevronDown,
  Shield,
  Activity
} from "lucide-react";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-rose border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-rose flex items-center justify-center">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">MediaTNT</span>
              <span className="px-2 py-0.5 rounded bg-rose/20 text-rose text-xs font-medium">Admin</span>
            </Link>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-night-paper transition-colors">
                    <div className="w-8 h-8 rounded-full bg-rose/20 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-rose" />
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-night-paper border-night-subtle">
                  <DropdownMenuItem className="text-muted-foreground">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-rose" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-white mb-1">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            System overview and user management
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "text-primary" },
            { label: "Campaigns", value: stats?.total_campaigns || 0, icon: Target, color: "text-emerald" },
            { label: "Properties", value: stats?.total_properties || 0, icon: Globe, color: "text-yellow-500" },
            { label: "Impressions", value: (stats?.total_impressions || 0).toLocaleString(), icon: Activity, color: "text-rose" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="p-6 rounded-lg bg-night-paper border border-night-subtle card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`admin-stat-${index}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">{stat.label}</span>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div className="font-heading text-2xl font-bold text-white">{stat.value}</div>
            </motion.div>
          ))}
        </div>

        {/* Users Table */}
        <div className="rounded-lg bg-night-paper border border-night-subtle overflow-hidden">
          <div className="p-6 border-b border-night-subtle">
            <h2 className="font-heading text-lg font-semibold text-white">All Users</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Balance</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {u.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-white">{u.name}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.role === "admin" ? "bg-rose/20 text-rose" :
                        u.role === "publisher" ? "bg-emerald/20 text-emerald" :
                        "bg-primary/20 text-primary"
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="text-muted-foreground">{u.company_name || "-"}</td>
                    <td className="font-mono">${u.balance?.toFixed(2) || "0.00"}</td>
                    <td className="text-muted-foreground text-sm">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
